import React from 'react';
import { styled } from '@compiled/react';
import { Checkbox } from '@atlaskit/checkbox';
import { xcss, Box } from '@atlaskit/primitives';
import type { OptionProps } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import type { CheckboxData } from '../../../common/ui/user-roles/types';

export type Props = OptionProps<CheckboxData> & {
	data: CheckboxData;
};

/**
 * A (significantly) simplified version of [Checkbox](src/packages/permissions-administration/pages/users-and-roles/src/common/ui/roles-picker/option/checkbox/index.tsx).
 * Much of the logic in the original component is not needed, as the checkbox will always be disabled in our use case.
 */
export const DropdownOption = (props: Props) => {
	const { data } = props;

	const { id, title, description, isChecked, isDisabled } = data;

	return (
		<OptionContainer key={`roles-${id}`}>
			<Checkbox isChecked={isChecked} isDisabled={isDisabled} />
			<ContentWrapper>
				<Title>{title}</Title>
				<Box xcss={descriptionStyles}>{description}</Box>
			</ContentWrapper>
		</OptionContainer>
	);
};

export default DropdownOption;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OptionContainer = styled.div({
	cursor: 'not-allowed',
	display: 'flex',
	flexDirection: 'row',
	padding: `${token('space.100', '8px')} ${token('space.250', '20px')}`,

	'&:hover': {
		background: token('color.background.neutral.subtle.hovered', '#091e420f'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Title = styled.div({
	cursor: 'inherit',
	fontWeight: 500,
	color: token('color.text.disabled', '#091e424f'),
	paddingBottom: token('space.050', '4px'),
	lineHeight: token('space.300', '24px'),
	wordBreak: 'break-word',
});

const descriptionStyles = xcss({
	cursor: 'inherit',
	color: 'color.text.disabled',
	font: token('font.body.UNSAFE_small'),
	wordBreak: 'break-word',
	lineHeight: '20px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	flex: 1,
	paddingLeft: token('space.025', '2px'),
});
